import { capitalCase } from 'capital-case';
import { lockToggle } from '../commands/ComponentCommands';
import { togglePropertyStripMode } from '../commands/ManipulationCommands';
import { ContextMenuWidget } from '../contextMenu/ContextMenuWidget';
import { Physical } from '../entities/Physical';
import { TextAlignment, TextSettings } from '../entities/TextConventions';
import { FontFamilyWidget } from './FontFamilyWidget';
import { FontSizeWidget } from './FontSizeWidget';
import { HasId } from './PropertyFacade';
import { PropertyStripLayout } from './PropertyStripLayout';

type TextObject = TextSettings & HasId;

const createTextAlignLayout = (abc: [string, string, string], prop: string): PropertyStripLayout<any> =>
    (d, s, o, w, a) => {

        const strip = w.createButtonStrip({
            name: prop,
            text: prop === 'textAlignH' ? 'Text Justification' : 'Vertical Alignment',
            layout: 'row',
        });

        const spec = [
            [abc[0], TextAlignment.start] as const,
            [abc[1], TextAlignment.middle] as const,
            [abc[2], TextAlignment.end] as const,
        ];

        const toLabel = (id: string) => capitalCase(id.slice(id[0] === 'v' ? 1 : 0));

        const items = spec.map(([id, av]) => {
            const name = `align:${id}`;
            const image = `deqo://icons/d2/${id}`;
            return w.createButton({
                name,
                image: `deqo://icons/d2/${id}`,
                text: toLabel(id),
                active: a.get(prop) === av,
                onAction: () => {
                    o.update(() => a.set(prop, av));
                    items.forEach(x => x.update({ active: x.name === name }));
                    strip.update({ image });
                },
            });
        });

        strip.update({ items, image: (items.find(x => x.active) ?? items[0])?.image });
        return [strip];
    };

export const textAlignHSelector = (propertyName = 'textAlignH') =>
    createTextAlignLayout(['align-left', 'align-center', 'align-right'], propertyName);
export const textAlignVSelector = (propertyName = 'textAlignV') =>
    createTextAlignLayout(['valign-top', 'valign-middle', 'valign-bottom'], propertyName);

export const fontFamilySelector = (propertyName = 'fontFamily') =>
    PropertyStripLayout<any>((d, s, o, w, a) => [
        w.createPopout({
            name: propertyName,
            ui: FontFamilyWidget,
            value: a.get(propertyName),
            image: 'deqo://icons/d2/font',
            text: 'Text Font',
            onSelect() {
                o.update(() => a.set(propertyName, this.value ?? 'Inter'));
            },
        })
    ]);

export const fontColorSelector = (propertyName = 'fontColor') =>
    PropertyStripLayout<any>((d, s, o, w, a) => [
        w.createColorSelector({
            allowNull: true,
            autoSelect: true,
            groups: true,
            name: propertyName,
            text: 'Text Color',
            value: a.get(propertyName),
            onSelect() {
                o.update(u => {
                    u.rolling(a.keyFor(propertyName));
                    a.set(propertyName, this.value);
                });
            },
        })
    ]);


export const fontSizeSelector = (propertyName = 'fontSize') =>
    PropertyStripLayout<any>((d, s, o, w, a) => [
        w.createPopout({
            name: propertyName,
            value: a.get(propertyName),
            image: 'deqo://icons/d2/text-size',
            text: 'Text Size',
            ui: FontSizeWidget,
            onSelect() {
                o.update(u => {
                    u.rolling(a.keyFor(propertyName));
                    a.set(propertyName, this.value);
                });
            },
        })
    ]);

// export const fontWeightButton: PropertyStripItem<TextObject> = (d, s, o, w, a) =>
//     w.createButton({
//         name: 'fontWeight',
//         selected: a.get('fontWeight') > 400,
//         image: 'deqo://icons/sln/01-interface-essential/text-bold',
//         action() {
//             o.update(() => a.set('fontWeight', x => x > 400 ? 400 : 500));
//             this.update({ selected: a.get('fontWeight') > 400 });
//         },
//     });

export const textSettingsLayout = PropertyStripLayout<TextObject>((d, s, o, w, a) => [
    ...fontColorSelector()(d, s, o, w, a),
    ...fontFamilySelector()(d, s, o, w, a),
    ...fontSizeSelector()(d, s, o, w, a),
    ...textAlignHSelector()(d, s, o, w, a),
    ...textAlignVSelector()(d, s, o, w, a),
    // fontWeightButton(d, s, o, wt),
]);

export const editText = PropertyStripLayout<Physical>((d, s, o, w, a) => [
    //
    // Edit Text
    w.createButton({
        name: 'editText',
        image: 'deqo://icons/i8/text',
        text: 'Edit Text',
        onAction() {
            d(togglePropertyStripMode);
        },
    }),
]);

export const lockAndMoreLayout = PropertyStripLayout((d, s, o, w, a) => [
    w.createButton({
        image: 'deqo://icons/d2/locked',
        text: 'Lock',
        onAction() {
            d(lockToggle);
        }
    }),
    w.createPopout({
        image: 'deqo://icons/hero/dots-vertical',
        text: 'More...',
        ui: ContextMenuWidget,
        onClose() {
            this.update({ open: false });
        },
    })
]);

export const moreLayout = PropertyStripLayout((d, s, o, w, a) => [
    w.createPopout({
        image: 'deqo://icons/hero/dots-vertical',
        text: 'More...',
        ui: ContextMenuWidget,
        onClose() {
            this.update({ open: false });
        },
    })
]);
